import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import TruncateText from "../../components/TruncateText";
import SearchInput from "../../components/SearchInput";
import Table from "../../components/Table";
import Pagination from "../../components/Pagination";
import EmptyState from "../../components/EmptyState";
import Button from "../../components/Button";
import Filters from "../../components/Filters";
import ListFilter from "../../components/Filters/ListFilter";
import messages from "../../assets/locale/messages";
import noImage from "../../assets/images/placeholders/noProductImagePlacholder.svg";
import noCategoryImg from "../../assets/images/placeholders/noCategoryImagePlaceholder.svg";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { ListDeleteIcon, ListEditIcon } from "../../utils/Icons";
import { categoriesLookupRequest } from "../../store/Lookups/actions";
import { subCategoriesListRequest } from "../../store/SubCategories/actions";
import DeleteSubCategory from "./DeleteSubCategory";
import { hasPermission, PERMISSIONS_IDS } from "../../utils/Roles";
import "./SubCategories.scss";

const SubCategories = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { subCategories, shared } = messages[lang];

  const subCategoriesList = useSelector(
    (state) => state.subCategories.list
  );
  const subCategoriesMeta = useSelector(
    (state) => state.subCategories.meta
  );
  const categoriesLookup = useSelector(
    (state) => state.lookups.categories.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilterValue, setCategoryFilterValue] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [deletedSubCategoryDetails, setDeletedSubCategoryDetails] =
    useState({});
  const [deleteSubCategoryModalOpen, setDeleteSubCategoryModalOpen] =
    useState(false);
  const [paramsObject, setParamsObject] = useState({
    category_ids: []
  });

  useEffect(() => {
    dispatch(categoriesLookupRequest());
  }, []);

  useEffect(() => {
    if (clearFilters) {
      setCategoryFilterValue([]);
      setParamsObject({
        category_ids: []
      });
    }
  }, [clearFilters]);

  const requestSubCategoriesList = () => {
    dispatch(
      subCategoriesListRequest({
        params: {
          page,
          search_value: searchValue,
          ...paramsObject
        }
      })
    );
  };

  const handleDeleteSubCategory = (name, id) => {
    setDeletedSubCategoryDetails({
      id,
      name,
      src: ROUTE_PATHS.categories,
      params: {
        page,
        search_value: searchValue,
        ...paramsObject
      }
    });
    setDeleteSubCategoryModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteSubCategoryModalOpen(false);
    setDeletedSubCategoryDetails({});
  };

  useEffect(() => {
    requestSubCategoriesList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestSubCategoriesList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        category_ids: categoryFilterValue
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  const onImageError = (e) => {
    e.target.src = noImage;
  };

  const renderSubCategoriesTableRow = ({
    Id,
    AdminCoverImage,
    Name,
    Description,
    CategoryName,
    ProductsNumber
  }) => (
    <TableRow
      key={`request-row-${Id}`}
      onClick={() => {
        navigate(ROUTE_PATHS.subcategoryDetails.replace(":id", Id));
      }}>
      <TableCell>
        <div className="d-flex align-items-center gap-3 pointer">
          <img
            src={AdminCoverImage || noImage}
            onError={onImageError}
            alt="subCategoryImage"
            className="category-img faint-grey-border border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>
        <TruncateText
          showTooltip={false}
          text={Description || "__"}
        />
      </TableCell>
      <TableCell>
        <TruncateText
          showTooltip={false}
          text={CategoryName || "__"}
        />
      </TableCell>
      <TableCell>{`${ProductsNumber || 0} ${subCategories.list.products}`}</TableCell>
      {(hasPermission(PERMISSIONS_IDS.sub_category_edit) ||
        hasPermission(PERMISSIONS_IDS.sub_category_delete)) && (
        <TableCell>
          <div className="w-100 d-flex justify-content-start ps-1 gap-2">
            {hasPermission(PERMISSIONS_IDS.sub_category_edit) && (
              <ListEditIcon
                className={"list-action-icon pointer"}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    ROUTE_PATHS.editSubCategories.replace(":id", Id)
                  );
                }}
              />
            )}
            {hasPermission(PERMISSIONS_IDS.sub_category_delete) && (
              <ListDeleteIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSubCategory(Name, Id);
                }}
                className={"list-delete-action-icon pointer"}
              />
            )}
          </div>
        </TableCell>
      )}
    </TableRow>
  );

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="py-3 px-md-5 px-3">
        <ListFilter
          list={categoriesLookup}
          name="categories"
          label={subCategories.list.filters.categories}
          selectedOptionsIds={categoryFilterValue}
          setSelectedOptionsIds={setCategoryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          searchPlaceholder={
            subCategories.list.filters.placeholders.categories
          }
          filterModalOpen={filterModalOpen}
        />
      </div>
    </div>
  );

  return (
    <div className="sub-categories-list-container">
      <div className="page-title pb-md-4 pb-3 d-md-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-flex justify-content-center align-items-md-center align-items-start flex-column flex-md-row">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {subCategories.list.subCategoriesList}
            </Typography>
          </div>

          <div className="block-title text-secondary">
            <Typography>{`(${subCategoriesMeta?.count ? subCategoriesMeta?.count : "0"} ${subCategoriesMeta?.count === 1 ? subCategories.list.subCategory : subCategories.list.subCategories})`}</Typography>
          </div>
        </div>
        <div className="title-buttons-container d-flex justify-content-md-center justify-content-end gap-2 gap-md-1 align-items-center">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={(action) => {
              setPage(1);
              setIsApplyFilters(action);
            }}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
          {hasPermission(PERMISSIONS_IDS.sub_category_add) && (
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="medium" />
                  <span className="ps-1 d-none d-md-block">
                    {subCategories.list.addMoreSubCategories}
                  </span>
                </span>
              }
              onClick={() => navigate(ROUTE_PATHS.addSubCategories)}
              customSpacing={"px-2 px-md-4"}
            />
          )}
        </div>
      </div>
      <div className="sub-categories-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 bg-white border-radius-8">
        <div className="table-header d-flex px-4 align-items-center mb-4">
          <SearchInput
            name="CategorySearch"
            id="CategorySearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass="search-input-wrapper search-input-wrapper-width"
            placeholder={subCategories.list.searchPlaceholder}
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {subCategoriesList?.length ? (
              <Table
                className={"px-0"}
                headlines={[
                  subCategories.list.tableHeader.subCategoryName,
                  subCategories.list.tableHeader.description,
                  subCategories.list.tableHeader.category,
                  subCategories.list.tableHeader.numberOfProducts,
                  (hasPermission(PERMISSIONS_IDS.sub_category_edit) ||
                    hasPermission(
                      PERMISSIONS_IDS.sub_category_delete
                    )) &&
                    shared.actions
                ]}
                rows={
                  !isLoading &&
                  subCategoriesList &&
                  subCategoriesList.map((category) =>
                    renderSubCategoriesTableRow(category)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      !!searchValue && !subCategoriesList?.length
                        ? subCategories.list.noSearchResults
                        : subCategories.list.noSubCategoriesFound
                    }
                    showActionButton={
                      !searchValue && subCategoriesList?.length === 0
                    }
                    ButtonLabel={
                      <span className="d-flex align-items-center">
                        <Add fontSize="medium" />
                        <span className="ps-1 ">
                          {subCategories.list.addMoreSubCategories}
                        </span>
                      </span>
                    }
                    handleCreate={() =>
                      navigate(ROUTE_PATHS.addSubCategories)
                    }
                    imgSrc={noCategoryImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && subCategoriesList?.length > 0 && (
            <Pagination
              count={subCategoriesMeta?.pages}
              page={page || subCategoriesMeta.currentPage}
              handleChange={(page) => {
                setPage(page);
              }}
              defaultPage={1}
            />
          )}
        </div>
      </div>
      {deleteSubCategoryModalOpen && (
        <DeleteSubCategory
          open={deleteSubCategoryModalOpen}
          closeModal={handleCloseModal}
          subCategoryInfo={deletedSubCategoryDetails}
        />
      )}
    </div>
  );
};

export default SubCategories;
